import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./biographyMembers.css";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { extractErrorMessage } from "../../utils/extractErrorMessage";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

export default function BiographyMember() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("onboarding_code", "226");
    sessionStorage.setItem("onboarding_force_route", "/biography-member");
    sessionStorage.setItem("onboarding_force_status", "salon");
  }, []);

  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      ""
    ).trim();
  }, []);

  const handleAddMember = () => {
    if (!email.trim()) {
      toast.error(t("auth.errorEnterEmail"));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("auth.errorInvalidEmail"));
      return;
    }

    if (members.includes(email)) {
      toast.error(t("auth.errorMemberAlreadyAdded"));
      return;
    }

    setMembers((prev) => [...prev, email]);
    setEmail("");
    toast.success(t("auth.successMemberAdded"));
  };

  const handleRemoveMember = (member) => {
    setMembers((prev) => prev.filter((m) => m !== member));
  };

  const handleNextOrSkip = async () => {
    if (members.length === 0) {
      localStorage.setItem("onboarding_code", "228");
      sessionStorage.setItem("onboarding_force_route", "/choose-niche");
      sessionStorage.setItem("onboarding_force_status", "salon");
      navigate("/choose-niche", { replace: true });
      return;
    }

    setLoading(true);

    const salonData = JSON.parse(localStorage.getItem("salonData") || "{}");

    try {
      if (!salonData.full_name || !salonData.company_name) {
        toast.error(t("auth.errorMissingSalonInfo"));
        navigate(-1);
        return;
      }

      if (!token) {
        toast.error(t("auth.errorNoToken"));
        navigate("/sign-in");
        return;
      }

      const userTimeZone = getUserTimeZone();

      const body = {
        ...salonData,
        worker_emails: members,
        timeZone: userTimeZone,
      };

      const userInfoUrl = `${BASE_URL}/auth/userinfo/`;

      const res = await fetch(userInfoUrl, {
        method: "PUT",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });

      if (res.status === 401) {
        toast.error(t("auth.errorSessionExpired"));
        navigate("/sign-in");
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("API error:", data);
        throw new Error(extractErrorMessage(data, t("auth.errorFailedSendMembers")));
      }

      localStorage.setItem("salonData", JSON.stringify(body));
      localStorage.setItem("onboarding_code", "228");

      sessionStorage.setItem("onboarding_force_route", "/choose-niche");
      sessionStorage.setItem("onboarding_force_status", "salon");

      toast.success(t("auth.successMembersAdded"));
      navigate("/choose-niche", { replace: true });
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.message || t("auth.errorFailedSendMembers"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && email.trim() && !loading) {
        e.preventDefault();
        handleAddMember();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [email, loading]);

  useEffect(() => {
    const emailInput = document.querySelector(".biography-member-add-form input");
    if (emailInput) {
      emailInput.focus();
    }
  }, []);

  return (
    <motion.div
      className="biography-member"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <main className="biography-member-card">
        <div className="biography-member-header">
          <div className="biography-member-text">
            <h2>{t("auth.biographyMemberHeader")}</h2>
            <p>{t("auth.biographyMemberSubheader")}</p>
          </div>
        </div>

        <div className="biography-member-content">
          <label className="biography-member-label">
            {t("auth.addMemberEmailLabel")}
          </label>
          <p className="biography-member-subtitle">
            {t("auth.addMemberEmailSubtitle")}
          </p>

          <div className="biography-member-add-form">
            <input
              type="email"
              placeholder={t("auth.memberEmailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email.trim()) {
                  e.preventDefault();
                  handleAddMember();
                }
              }}
            />
            <button
              className="biography-member-add-btn"
              onClick={handleAddMember}
              disabled={!email.trim() || loading}
            >
              {t("auth.addMemberButton")} <FiPlus size={16} />
            </button>
          </div>

          <div className="biography-member-list">
            {members.length === 0 ? (
              <div className="biography-member-empty-state">
                <p>{t("auth.noMembersYet")}</p>
                <small>{t("auth.noMembersHint")}</small>
              </div>
            ) : (
              members.map((member, i) => (
                <div key={i} className="biography-member-item">
                  <span className="biography-member-email">{member}</span>
                  <button
                    className="biography-member-delete"
                    onClick={() => handleRemoveMember(member)}
                    aria-label={`Remove ${member}`}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="biography-member-footer-buttons">
          <motion.button
            className="biography-member-skip"
            onClick={handleNextOrSkip}
            disabled={loading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {loading
              ? t("auth.saving")
              : members.length > 0
              ? t("auth.next")
              : t("auth.skipButton")}
          </motion.button>
          <span className="biography-member-step">{t("auth.stepIndicator")}</span>
        </div>
      </main>

      <footer className="biography-member-footer">
        <span>{t("auth.copyright")}</span>
        <div className="biography-member-footer-links">
          <a href="#">
            <IoMdLock /> {t("auth.privacy")}
          </a>
          <a href="#">
            <CiCircleQuestion /> {t("auth.getHelp")}
          </a>
        </div>
      </footer>
    </motion.div>
  );
}
